import { Dispatch, SetStateAction, useCallback } from 'react';
import * as Types from '../types/types';
import { ApiClient, GuestConversionRequest } from 'services/api-client';
import { AuthState, LoginRequest } from './authTypes';
import {
  clearStoredAuth,
  getStoredAuthData,
  storeAuthData,
  updateStoredUserData,
} from './authStorage';

interface PrimaryAuthActionDeps {
  authState: AuthState;
  setAuthState: Dispatch<SetStateAction<AuthState>>;
  apiClient: ApiClient;
  createGuestSession: () => Promise<boolean>;
  resetGuestCreationAttempt: () => void;
}

export function useAuthPrimaryActions({
  authState,
  setAuthState,
  apiClient,
  createGuestSession,
  resetGuestCreationAttempt,
}: PrimaryAuthActionDeps): {
  login: (loginData: LoginRequest) => Promise<boolean>;
  convertGuestToUser: (registrationData: GuestConversionRequest) => Promise<boolean>;
  verifyMFA: (mfaData: Types.MFAVerifyRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUserData: (updatedUser: Types.User) => void;
} {
  const login = useCallback(
    async (loginData: LoginRequest): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null, mfaResponse: null }));

      try {
        const result = await apiClient.login({
          login: loginData.login,
          password: loginData.password,
        });

        if ('mfaRequired' in result) {
          setAuthState(prev => ({ ...prev, isLoading: false, mfaResponse: result }));
          return false;
        }

        const authResponse: Types.AuthResponse = result;
        storeAuthData(authResponse, false);
        apiClient.setAuthToken(authResponse.accessToken);
        setAuthState(prev => ({
          ...prev,
          user: authResponse.user,
          guest: null,
          isAuthenticated: true,
          isGuest: false,
          isLoading: false,
          error: null,
          mfaResponse: null,
        }));
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Network error occurred. Please try again.';
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: errorMessage,
          mfaResponse: null,
        }));
        return false;
      }
    },
    [apiClient, setAuthState]
  );

  const convertGuestToUser = useCallback(
    async (registrationData: GuestConversionRequest): Promise<boolean> => {
      if (!authState.isGuest || !authState.guest) {
        throw new Error('Not currently a guest user');
      }

      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await apiClient.convertGuestToUser(registrationData);
        storeAuthData(result.auth, false);
        apiClient.setAuthToken(result.auth.accessToken);
        setAuthState(prev => ({
          ...prev,
          user: result.auth.user,
          guest: null,
          isAuthenticated: true,
          isGuest: false,
          isLoading: false,
          error: null,
        }));
        return true;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to convert guest account';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, authState.guest, authState.isGuest, setAuthState]
  );

  const verifyMFA = useCallback(
    async (mfaData: Types.MFAVerifyRequest): Promise<boolean> => {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await apiClient.verifyMFA(mfaData);
        if (!result.accessToken) {
          return false;
        }

        const authResponse: Types.AuthResponse = result;
        storeAuthData(authResponse, false);
        apiClient.setAuthToken(authResponse.accessToken);
        setAuthState(prev => ({
          ...prev,
          user: authResponse.user,
          guest: null,
          isAuthenticated: true,
          isGuest: false,
          isLoading: false,
          error: null,
          mfaResponse: null,
        }));
        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'MFA verification failed';
        setAuthState(prev => ({ ...prev, isLoading: false, error: errorMessage }));
        return false;
      }
    },
    [apiClient, setAuthState]
  );

  const logout = useCallback(async () => {
    try {
      if (authState.isAuthenticated && !authState.isGuest) {
        const stored = getStoredAuthData();
        if (stored.accessToken && stored.refreshToken) {
          await apiClient.logout(stored.accessToken, stored.refreshToken).catch(() => undefined);
        }
      }
    } finally {
      clearStoredAuth();
      apiClient.clearAuthToken();
      resetGuestCreationAttempt();
      await createGuestSession();
    }
  }, [
    apiClient,
    authState.isAuthenticated,
    authState.isGuest,
    createGuestSession,
    resetGuestCreationAttempt,
  ]);

  const updateUserData = useCallback(
    (updatedUser: Types.User) => {
      updateStoredUserData(updatedUser);
      setAuthState(prev => ({
        ...prev,
        user: authState.isGuest ? null : updatedUser,
        guest: authState.isGuest ? (updatedUser as Types.Guest) : prev.guest,
      }));
    },
    [authState.isGuest, setAuthState]
  );

  return { login, convertGuestToUser, verifyMFA, logout, updateUserData };
}
